import clsx from 'clsx'
import React from 'react'

import { Shimmer } from '@wartek-id/shimmer'

import type { HTMLProps } from 'react'

const Header = (
  { children, className, ...otherProps }: HTMLProps<HTMLTableSectionElement>,
  ref: React.Ref<HTMLTableSectionElement> | undefined
) => {
  return (
    <thead
      ref={ref}
      {...otherProps}
      className={clsx(
        'bg-gray-5 !text-left border-b border-gray-20',
        className
      )}
    >
      <tr>{children}</tr>
    </thead>
  )
}

const HeaderItem = (
  { children, className, ...otherProps }: HTMLProps<HTMLTableCellElement>,
  ref: React.Ref<HTMLTableCellElement> | undefined
) => {
  return (
    <th
      ref={ref}
      {...otherProps}
      className={clsx('p-4 print:p-2', '!font-semibold text-sm', className)}
    >
      {children}
    </th>
  )
}

const Body = (
  { children, ...otherProps }: HTMLProps<HTMLTableSectionElement>,
  ref: React.Ref<HTMLTableSectionElement> | undefined
) => {
  return (
    <tbody ref={ref} {...otherProps}>
      {children}
    </tbody>
  )
}

const Row = (
  {
    children,
    className,
    bordered = true,
    striped = false,
    ...otherProps
  }: HTMLProps<HTMLTableRowElement> & { bordered?: boolean; striped?: boolean },
  ref: React.Ref<HTMLTableRowElement> | undefined
) => {
  return (
    <tr
      data-testid="row"
      ref={ref}
      {...otherProps}
      className={clsx(
        'align-top',
        bordered && 'border-b last:border-none border-gray-20',
        striped && 'even:bg-gray-5',
        className
      )}
    >
      {children}
    </tr>
  )
}

const RowItem = (
  { children, className, ...otherProps }: HTMLProps<HTMLTableCellElement>,
  ref: React.Ref<HTMLTableCellElement> | undefined
) => {
  return (
    <td
      ref={ref}
      {...otherProps}
      className={clsx('p-4 print:p-2', 'text-sm', className)}
    >
      {children}
    </td>
  )
}

export const TableShimmer = ({
  bordered = true,
  columns = 3,
  rows = 3,
  height = '40px',
}: {
  bordered?: boolean
  columns?: number
  rows?: number
  height?: string
}) => {
  return (
    <>
      {Array(rows)
        .fill('')
        .map((_, i) => (
          <TableRow key={i} bordered={bordered}>
            {Array(columns)
              .fill('')
              .map((_, j) => (
                <TableRowItem
                  className={clsx('text-center align-middle', {
                    'pl-0': j > 0,
                  })}
                  key={`${i}-${j}`}
                >
                  <Shimmer height={height} width="100%" />
                </TableRowItem>
              ))}
          </TableRow>
        ))}
    </>
  )
}

type TableRootProps = {
  withOuterBorder?: boolean
  rounded?: boolean
  fullWidth?: boolean
} & HTMLProps<HTMLTableElement>

export const TableRoot = (
  {
    children,
    className,
    withOuterBorder = false,
    rounded = false,
    fullWidth = false,
    ...otherProps
  }: TableRootProps,
  ref: React.Ref<HTMLTableElement> | undefined
) => {
  return (
    <div
      className={clsx(
        withOuterBorder && 'border border-subdued overflow-x-auto',
        rounded && 'rounded-lg',
        fullWidth && 'w-full'
      )}
    >
      <table
        ref={ref}
        {...otherProps}
        className={clsx('w-full', className)}
        style={{
          scrollbarWidth: 'none',
        }}
      >
        {children}
      </table>
    </div>
  )
}

export const Table = React.forwardRef(TableRoot)
export const TableHeader = React.forwardRef(Header)
export const TableHeaderItem = React.forwardRef(HeaderItem)
export const TableBody = React.forwardRef(Body)
export const TableRow = React.forwardRef(Row)
export const TableRowItem = React.forwardRef(RowItem)
